import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  async function handleRequest(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api("/api/v2/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
    } catch (err) {
      // Swallow all errors — anti-enumeration: always show confirmation
      if (!(err instanceof ApiError)) {
        setError(t("resetPassword.genericError"));
        setLoading(false);
        return;
      }
    } finally {
      setLoading(false);
    }
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{t("resetPassword.checkEmailTitle")}</CardTitle>
            <CardDescription>{t("resetPassword.codeSentSubtitle")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              {t("resetPassword.antiEnumMessage")}
            </p>
            <Button
              className="w-full"
              onClick={() =>
                navigate(`/reset-password/confirm?email=${encodeURIComponent(email)}`)
              }
            >
              {t("resetPassword.enterCodeCta")}
            </Button>
            <div className="text-center text-sm">
              <Link to="/login" className="text-muted-foreground hover:text-foreground">
                {t("resetPassword.backToLogin")}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{t("resetPassword.title")}</CardTitle>
          <CardDescription>
            {t("resetPassword.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRequest} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("resetPassword.email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="animate-spin" />
                  {t("resetPassword.sending")}
                </>
              ) : (
                t("resetPassword.submit")
              )}
            </Button>
            <div className="text-center text-sm">
              <Link to="/login" className="text-muted-foreground hover:text-foreground">
                {t("resetPassword.backToLogin")}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
